import { isObject, cleanString, numberOrNull, booleanValue, stringArray } from './helpers';

/* ============================================================
   HOUSE RULES - FRONTEND -> BACKEND
============================================================ */

export function transformHouseRules(rules) {
    const result = {
        smokingAllowed: false,
        petsAllowed: false,
        partiesAllowed: false,
        childrenAllowed: true,
        visitorsAllowed: false,
        quietHoursStart: '',
        quietHoursEnd: '',
        customRules: []
    };

    if (isObject(rules)) {
        return {
            smokingAllowed: booleanValue(rules.smokingAllowed, false),

            petsAllowed: booleanValue(rules.petsAllowed, false),

            partiesAllowed: booleanValue(rules.partiesAllowed, false),

            childrenAllowed: booleanValue(rules.childrenAllowed, true),

            visitorsAllowed: booleanValue(rules.visitorsAllowed, false),

            quietHoursStart: cleanString(rules.quietHoursStart),

            quietHoursEnd: cleanString(rules.quietHoursEnd),

            customRules: stringArray(rules.customRules),

            selectedRules: stringArray(rules.selectedRules)
        };
    }

    const rulesArray = stringArray(rules);

    // the exact tags picked in the Policies step, so the form shows the same ones on edit
    result.selectedRules = [...rulesArray];

    rulesArray.forEach((rule) => {
        const lower = rule.toLowerCase();

        if (lower.includes('no smoking')) {
            result.smokingAllowed = false;
        } else if (lower.includes('smoking allowed')) {
            result.smokingAllowed = true;
        } else if (lower.includes('children allowed')) {
            result.childrenAllowed = true;
        } else if (lower.includes('children not allowed')) {
            result.childrenAllowed = false;
        } else if (lower.includes('couples allowed')) {
            result.customRules.push(rule);
        } else if (lower.includes('pets allowed')) {
            result.petsAllowed = true;
        } else if (lower.includes('no outside guests')) {
            result.visitorsAllowed = false;
        } else if (lower.includes('outside guests allowed')) {
            result.visitorsAllowed = true;
        } else if (lower.includes('parties allowed')) {
            result.partiesAllowed = true;
        } else {
            result.customRules.push(rule);
        }
    });

    return result;
}

/* ============================================================
   HOUSE RULES - BACKEND -> FRONTEND
   ------------------------------------------------------------
   The Policies step wants a flat array of preset strings (plus
   any freeform custom rule text) rather than the backend's
   structured booleans. This reconstructs the closest matching
   preset tags. "Smoking allowed", "Parties allowed" and
   "Outside guests allowed" have no matching preset in the
   Policies step's option list, so a true value for those flags
   can't be shown as a tag - that's a gap in the preset list,
   not something fixable here without adding new presets.
============================================================ */

export function reverseHouseRules(rules = {}) {
    if (Array.isArray(rules)) {
        return stringArray(rules);
    }

    const tags = [];

    if (!isObject(rules)) {
        return tags;
    }

    // saved since the tags are stored as picked
    const selected = stringArray(rules.selectedRules);
    if (selected.length) return selected;

    /*
     * Older records: only the flags that differ from the defaults become
     * tags (a default like "no smoking" can't be told apart from "never
     * picked", and showing it for every record made it look chosen).
     */
    if (rules.petsAllowed === true) tags.push('Pets allowed');
    if (rules.childrenAllowed === false) tags.push('Children not allowed');
    if (rules.smokingAllowed === true) tags.push('Smoking allowed');
    if (rules.partiesAllowed === true) tags.push('Parties allowed');
    if (rules.visitorsAllowed === true) tags.push('Outside guests allowed');

    stringArray(rules.customRules).forEach((rule) => {
        if (!tags.includes(rule)) {
            tags.push(rule);
        }
    });

    return tags;
}

/* ============================================================
   CANCELLATION - FRONTEND -> BACKEND
   ------------------------------------------------------------
   FIX: the Policies step collects THREE numbers -
   freeCancellationDays, refundPercent AND partialRefundPercent
   - but this used to only ever build ONE refund rule from the
   first two, so partialRefundPercent was silently dropped and
   never reached the backend. It's now sent as a second rule
   (beforeDays: 0, meaning "any time up to check-in") so both
   values survive and can be read back by reverseCancellation.
============================================================ */

export function transformCancellation(cancellation = {}) {
    if (!isObject(cancellation)) {
        return {};
    }

    const refundRules = [];

    if (Array.isArray(cancellation.refundRules) && cancellation.refundRules.length) {
        cancellation.refundRules.forEach((rule) => {
            refundRules.push({
                beforeDays: numberOrNull(rule.beforeDays) || 0,

                refundPercent: Math.max(0, Math.min(100, numberOrNull(rule.refundPercent) || 0))
            });
        });
    } else {
        /*
         * Support the Policies step's structure:
         * freeCancellationDays / refundPercent / partialRefundPercent
         */

        if (cancellation.freeCancellationDays !== undefined && cancellation.refundPercent !== undefined) {
            refundRules.push({
                beforeDays: Math.max(0, numberOrNull(cancellation.freeCancellationDays) || 0),

                refundPercent: Math.max(0, Math.min(100, numberOrNull(cancellation.refundPercent) || 0))
            });
        }

        if (cancellation.partialRefundPercent !== undefined) {
            refundRules.push({
                beforeDays: 0,

                refundPercent: Math.max(0, Math.min(100, numberOrNull(cancellation.partialRefundPercent) || 0))
            });
        }
    }

    return {
        type: cancellation.type || 'moderate',

        description: cleanString(cancellation.description),

        refundRules,

        cancellationFee: numberOrNull(cancellation.cancellationFee) || 0,

        nonRefundableAfterCheckIn: booleanValue(cancellation.nonRefundableAfterCheckIn, true)
    };
}

/* ============================================================
   CANCELLATION - BACKEND -> FRONTEND
============================================================ */

export function reverseCancellation(cancellation = {}) {
    const rules = Array.isArray(cancellation?.refundRules) ? cancellation.refundRules : [];

    return {
        type: cancellation?.type || 'moderate',
        freeCancellationDays: numberOrNull(rules[0]?.beforeDays) ?? 7,
        refundPercent: numberOrNull(rules[0]?.refundPercent) ?? 100,
        partialRefundPercent: numberOrNull(rules[1]?.refundPercent) ?? 50,
        nonRefundableAfterCheckIn: cancellation?.nonRefundableAfterCheckIn !== false,
        // not edited in the form — kept as saved
        description: cleanString(cancellation?.description),
        cancellationFee: numberOrNull(cancellation?.cancellationFee) || 0
    };
}

/* ============================================================
   POLICIES - FRONTEND -> BACKEND
============================================================ */

export function transformPolicies(policies = {}) {
    return {
        checkInTime: cleanString(policies.checkInTime || policies.checkIn) || '14:00',

        checkOutTime: cleanString(policies.checkOutTime || policies.checkOut) || '11:00',

        earlyCheckIn: {
            enabled: booleanValue(policies.earlyCheckIn, false),

            fee: numberOrNull(policies.earlyCheckInFee) || 0
        },

        lateCheckOut: {
            enabled: booleanValue(policies.lateCheckOut, false),

            fee: numberOrNull(policies.lateCheckOutFee) || 0
        },

        // minStay / maxStay = what the Policies step edits (minimumStay /
        // maximumStay are the unused starting defaults — they used to win)
        minStayNights: Math.max(numberOrNull(policies.minStay ?? policies.minimumStay) || 1, 1),

        // 0 / blank in the form = no limit (saved as null, never 0)
        maxStayNights: numberOrNull(policies.maxStay ?? policies.maximumStay) || null,

        advanceBookingDays: Math.max(numberOrNull(policies.advanceBookingDays) ?? 365, 0),

        sameDayBooking: booleanValue(policies.sameDayBooking, true),

        instantBooking: booleanValue(policies.instantBooking, true),

        cancellation: transformCancellation(policies.cancellation),

        houseRules: transformHouseRules(policies.houseRules)
    };
}

/* ============================================================
   POLICIES - BACKEND -> FRONTEND
   ------------------------------------------------------------
   Targets the shape PoliciesSection.jsx actually reads/writes
   (its own normalizePolicies() default shape), which is what
   ends up on formData.policies at runtime - not the differently
   -named placeholder shape in AccommodationForm.jsx's
   initialFormData (checkIn/checkOut/minimumStay/...), which
   gets overwritten the moment PoliciesSection renders anyway.
============================================================ */

// "14:00" -> "2:00 PM" (the Policies step's time options); other text as is
export const to12h = (value) => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(cleanString(value));
    if (!match) return cleanString(value);
    const hours = Number(match[1]);
    return `${hours % 12 || 12}:${match[2]} ${hours < 12 ? 'AM' : 'PM'}`;
};

// "2:00 PM" -> "14:00" (for <input type="time">); "14:00" stays
export const to24h = (value) => {
    const text = cleanString(value);
    const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(text);
    if (!match) return /^\d{1,2}:\d{2}$/.test(text) ? text.padStart(5, '0') : '';
    let hours = Number(match[1]) % 12;
    if (match[3].toUpperCase() === 'PM') hours += 12;
    return `${String(hours).padStart(2, '0')}:${match[2]}`;
};

export function reversePolicies(policies = {}) {
    return {
        checkInTime: to12h(policies.checkInTime) || '2:00 PM',
        checkOutTime: to12h(policies.checkOutTime) || '11:00 AM',
        earlyCheckIn: booleanValue(policies.earlyCheckIn?.enabled, false),
        lateCheckOut: booleanValue(policies.lateCheckOut?.enabled, false),
        // not edited in the form — kept as saved
        earlyCheckInFee: numberOrNull(policies.earlyCheckIn?.fee) || 0,
        lateCheckOutFee: numberOrNull(policies.lateCheckOut?.fee) || 0,
        minStay: Math.max(numberOrNull(policies.minStayNights) || 1, 1),
        // no limit saved -> 0 ("no limit" in PoliciesSection), not 1 night
        maxStay: Math.max(numberOrNull(policies.maxStayNights) || 0, 0),
        advanceBookingDays: Math.max(numberOrNull(policies.advanceBookingDays) ?? 365, 0),
        sameDayBooking: booleanValue(policies.sameDayBooking, true),
        instantBooking: booleanValue(policies.instantBooking, true),
        cancellation: reverseCancellation(policies.cancellation),
        houseRules: reverseHouseRules(policies.houseRules)
    };
}

/* ============================================================
   BOOKING SETTINGS - FRONTEND -> BACKEND
   ------------------------------------------------------------
   NOTE: formData.bookingSettings isn't written to by any step
   in the wizard today (the Policies step writes everything to
   formData.policies instead), so this always sends the
   hardcoded initialFormData defaults regardless of what the
   organizer actually configured. It overlaps with policies.* in
   meaning (instantBooking, sameDayBooking, advanceBookingDays
   exist in both places). Left as-is since removing it is a
   backend-facing decision, but flagging it here since it's the
   same class of "collected differently than it's used" issue.
============================================================ */

export function transformBookingSettings(settings = {}) {
    return {
        instantBooking: booleanValue(settings.instantBooking, false),

        hostApprovalRequired: booleanValue(settings.requestToBook ?? settings.hostApprovalRequired, true),

        allowSameDayBooking: booleanValue(settings.sameDayBooking ?? settings.allowSameDayBooking, false),

        advanceBookingDays: Math.max(numberOrNull(settings.advanceBookingDays) ?? 365, 0),

        minimumAge: Math.max(numberOrNull(settings.minimumAge) || 0, 0),

        allowPartialPayment: booleanValue(settings.partialPayment ?? settings.allowPartialPayment, false),

        partialPaymentPercent: Math.max(0, Math.min(100, numberOrNull(settings.partialPaymentPercent) || 0))
    };
}
